import * as React from "react";
import {
  Typography,
  Box,
  IconButton,
  Grid,
  Button as MUIButton,
  Autocomplete,
  Container,
  Paper,
  Modal,
  Backdrop,
  Fade,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField
} from "@mui/material";
import { connect } from "react-redux";
import {
  MainInput,
  main_button,
} from "../../app/components/Styles";
import { brandFilterOptions, brandOptionLabel } from "../../app/components/BrandSelect";
import { Tag } from "antd";
import { FoCard, FoField, FoFilter, FoTable } from "./financeUi";
import { getPayouts, getVendors, getUploadUrl, addTransactionProof, deleteTransaction, deleteTransactionProof } from "../../app/store/actions/whFinanceActions";
import { useEffect } from "react";
import { Upload, CheckCircle, Cancel } from "@mui/icons-material";
import { notification } from "antd";
import moment from "moment";
import axios from "axios";
import DateRangePicker from "../../app/components/DateRangePicker";

export const Payouts = ({
  getPayouts,
  getVendors,
  getUploadUrl,
  addTransactionProof,
  deleteTransaction,
  deleteTransactionProof,
  user,
  transactions,
  payouts,
  vendors,
  isLoading,
  token
}) => {
  const [startDate, setStartDate] = React.useState(moment().startOf("month"))
  const [endDate, setEndDate] = React.useState(moment().endOf("month"))
  const [selectedVendor, setSelectedVendor] = React.useState(null)
  const [page, setPage] = React.useState(1)
  const [limit, setLimit] = React.useState(10)
  const [uploadModalOpen, setUploadModalOpen] = React.useState(false)
  const [selectedTransaction, setSelectedTransaction] = React.useState(null)
  const [uploading, setUploading] = React.useState(false)
  const [selectedFile, setSelectedFile] = React.useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false)
  const [deleteProofConfirmOpen, setDeleteProofConfirmOpen] = React.useState(false)
  const [transactionToDelete, setTransactionToDelete] = React.useState(null)
  const [deleting, setDeleting] = React.useState(false)
  const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false)
  const [password, setPassword] = React.useState('')
  const [passwordError, setPasswordError] = React.useState(null)
  const [passwordLoading, setPasswordLoading] = React.useState(false)
  const [pendingDeleteAction, setPendingDeleteAction] = React.useState(null) // 'transaction' or 'proof'

  const formatDateParam = (date) => date?.format?.("YYYY-MM-DD") ?? date;

  useEffect(() => {
    getVendors();
    getPayouts({ 
      startDate: formatDateParam(startDate), 
      endDate: formatDateParam(endDate), 
      type: "Payout",
      page,
      limit,
      vendorId: selectedVendor?._id
    })
  }, [])

  const handleFilter = () => {
    setPage(1); // Reset to first page when filtering
    getPayouts({ 
      startDate: formatDateParam(startDate), 
      endDate: formatDateParam(endDate), 
      type: "Payout",
      page: 1,
      limit,
      vendorId: selectedVendor?._id
    });
  }

  const handleTableChange = (newPage, newPageSize) => {
    const updatedPage = newPage || page;
    const updatedPageSize = newPageSize || limit;
    
    // Only update state and fetch if values actually changed
    if (updatedPage !== page || updatedPageSize !== limit) {
      setPage(updatedPage);
      setLimit(updatedPageSize);
      getPayouts({ 
        startDate: formatDateParam(startDate), 
        endDate: formatDateParam(endDate), 
        type: "Payout",
        page: updatedPage, 
        limit: updatedPageSize,
        vendorId: selectedVendor?._id
      });
    }
  }

  const handleOpenUploadModal = (record) => {
    setSelectedTransaction(record);
    setUploadModalOpen(true);
    setSelectedFile(null);
  }

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
    setSelectedTransaction(null);
    setSelectedFile(null);
  }

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const validPdfTypes = ['application/pdf'];
    
    if (!validImageTypes.includes(file.type) && !validPdfTypes.includes(file.type)) {
      notification.error({
        message: 'Invalid file type. Please upload an image or PDF file.',
        style: { marginTop: "10vh" }
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      notification.error({
        message: 'File is too large. Maximum size is 10MB.',
        style: { marginTop: "10vh" }
      });
      return;
    }

    setSelectedFile(file);
  }

  const handleUpload = async () => {
    if (!selectedFile || !selectedTransaction) return;

    setUploading(true);
    try {
      const fileExtension = selectedFile.name.split('.').pop();
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(7);
      const filename = `${timestamp}-${random}.${fileExtension}`;
      const key = `proofs/${filename}`;
      
      // Determine file type (image or pdf)
      const isImage = selectedFile.type.startsWith('image/');
      const fileType = isImage ? selectedFile.type : 'application/pdf';

      // Get presigned URL
      const urlData = await getUploadUrl({ fileType, key });
      const { uploadURL, publicURL } = urlData;

      // Upload file to S3 using presigned URL
      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': fileType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Save proof URL to transaction
      await addTransactionProof({
        transactionId: selectedTransaction._id,
        proof: publicURL
      });

      // Refresh the payouts list
      handleFilter();
      
      handleCloseUploadModal();
    } catch (error) {
      console.error('Upload error:', error);
      notification.error({
        message: error?.response?.data?.message || 'Failed to upload proof. Please try again.',
        style: { marginTop: "10vh" }
      });
    } finally {
      setUploading(false);
    }
  }

  const handleDeleteTransaction = (record) => {
    setTransactionToDelete(record);
    setPendingDeleteAction('transaction');
    setPasswordDialogOpen(true);
  }

  const handleDeleteProof = (record) => {
    setTransactionToDelete(record);
    setPendingDeleteAction('proof');
    setPasswordDialogOpen(true);
  }

  const handlePasswordClose = () => {
    if (!passwordLoading) {
      setPasswordDialogOpen(false);
      setPassword('');
      setPasswordError(null);
      setTransactionToDelete(null);
      setPendingDeleteAction(null);
    }
  }

  const handlePasswordConfirm = async () => {
    if (!password) {
      setPasswordError('Password is required.');
      return;
    }
    setPasswordLoading(true);
    setPasswordError(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/Finance/payouts/refreshPass`,
        { password: password },
        {
          headers: {
            "x-access-token": token
          },
        });

      if (response.status === 200) {
        setPasswordDialogOpen(false);
        setPassword('');
        // Now show the delete confirmation dialog
        if (pendingDeleteAction === 'transaction') {
          setDeleteConfirmOpen(true);
        } else if (pendingDeleteAction === 'proof') {
          setDeleteProofConfirmOpen(true);
        }
      } else {
        setPasswordError('Invalid password. Please try again.');
      }
    } catch (error) {
      setPasswordError(error.response?.data?.message || 'Unable to verify password. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  }

  const handleConfirmDeleteTransaction = async () => {
    if (!transactionToDelete) return;
    
    setDeleting(true);
    try {
      await deleteTransaction({ transactionId: transactionToDelete._id });
      setDeleteConfirmOpen(false);
      setTransactionToDelete(null);
      // Refresh the payouts list
      handleFilter();
    } catch (error) {
      console.error('Delete error:', error);
    } finally {
      setDeleting(false);
    }
  }

  const handleConfirmDeleteProof = async () => {
    if (!transactionToDelete) return;
    
    setDeleting(true);
    try {
      await deleteTransactionProof({ transactionId: transactionToDelete._id });
      setDeleteProofConfirmOpen(false);
      setTransactionToDelete(null);
      // Refresh the payouts list
      handleFilter();
    } catch (error) {
      console.error('Delete proof error:', error);
    } finally {
      setDeleting(false);
    }
  }

  const handleCloseDeleteConfirm = () => {
    if (!deleting) {
      setDeleteConfirmOpen(false);
      setTransactionToDelete(null);
      setPendingDeleteAction(null);
    }
  }

  const handleCloseDeleteProofConfirm = () => {
    if (!deleting) {
      setDeleteProofConfirmOpen(false);
      setTransactionToDelete(null);
      setPendingDeleteAction(null);
    }
  }

  const payoutColumns = [
    {
      title: 'Date Paid',
      dataIndex: 'date',
      sorter: (a, b) => { return moment(a.date).isBefore(b.date) ? -1 : 1 },
    },
    {
      title: 'Value',
      dataIndex: 'value',
      render: (_, { value }) => (
        <Typography
          component="span"
          sx={{ color: value < 0 ? 'error.main' : 'inherit', fontSize: 'inherit' }}
        >
          {value} EGP
        </Typography>
      ),
      sorter: (a, b) => { return a.value - b.value },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (_, { type }) => <Tag>{type}</Tag>
    },
    {
      title: 'Service',
      dataIndex: 'service',
      render: (_, { service }) => <Tag>{service}</Tag>
    },
    {
      title: 'Vendor',
      dataIndex: ['vendor', 'name'],
    },
    {
      title: 'Proof',
      dataIndex: 'proof',
      render: (_, record) => {
        if (record.proof) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <MUIButton
                size="small"
                variant="outlined"
                onClick={() => window.open(record.proof, '_blank')}
                sx={{
                  textTransform: 'none',
                  borderColor: 'var(--secColor)',
                  color: 'var(--secColor)',
                  '&:hover': {
                    borderColor: 'var(--secColor)',
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                View Proof
              </MUIButton>
              <MUIButton
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleDeleteProof(record)}
                sx={{
                  textTransform: 'none',
                }}
              >
                Delete Proof
              </MUIButton>
            </Box>
          );
        }
        return (
          <MUIButton
            size="small"
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => handleOpenUploadModal(record)}
            sx={{
              textTransform: 'none',
              borderColor: 'var(--secColor)',
              color: 'var(--secColor)',
              '&:hover': {
                borderColor: 'var(--secColor)',
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Upload Proof
          </MUIButton>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <MUIButton
          size="small"
          variant="outlined"
          color="error"
          onClick={() => handleDeleteTransaction(record)}
          sx={{
            textTransform: 'none',
          }}
        >
          Delete
        </MUIButton>
      ),
    },
  ];

  const payoutData = payouts?.docs?.map((row) => {
    return {
      _id: row._id,
      vendor: row.vendor,
      date: moment(row.createdAt).format("lll"),
      service: row.service,
      type: row.type,
      value: row.value,
      proof: row.proof
    }
  }) || []

  return (
    <div className="fo-stack">
      <FoFilter onApply={handleFilter} applying={isLoading}>
        <FoField label="Date range" grow>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={({ startDate: nextStart, endDate: nextEnd }) => {
              if (nextStart) setStartDate(nextStart);
              if (nextEnd) setEndDate(nextEnd);
            }}
            fullWidth
            margin="dense"
            placeholder="Select date range"
          />
        </FoField>
        <FoField label="Vendor">
          <Autocomplete
            options={vendors || []}
            value={selectedVendor}
            onChange={(_, value) => setSelectedVendor(value)}
            getOptionLabel={brandOptionLabel}
            filterOptions={brandFilterOptions}
            renderInput={(params) => (
              <MainInput {...params} fullWidth placeholder="Select vendor" />
            )}
          />
        </FoField>
      </FoFilter>

      <FoCard title="Payouts" count={`${payouts?.totalDocs || 0} payouts`} note="Proof upload and delete stay on each row. Stage workflow is not stored yet.">
        <FoTable
          columns={payoutColumns}
          dataSource={payoutData}
          loading={isLoading}
          rowKey="_id"
          empty="No payouts in this view."
          pagination={{
            current: page,
            pageSize: limit,
            total: payouts?.totalDocs || 0,
            onChange: (newPage, newPageSize) => handleTableChange(newPage, newPageSize),
          }}
        />
      </FoCard>

      {/* Upload Proof Modal */}
      <Modal
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={uploadModalOpen}>
          <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 500 },
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              outline: 'none',
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "var(--secColor)" }}>
              Upload Proof
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                Select File (Image or PDF)
              </Typography>
              
              {!selectedFile ? (
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    '&:hover': {
                      borderColor: uploading ? 'divider' : 'var(--secColor)',
                      bgcolor: uploading ? 'rgba(0, 0, 0, 0.02)' : 'rgba(0, 0, 0, 0.04)',
                    },
                    opacity: uploading ? 0.6 : 1,
                  }}
                >
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    style={{ display: 'none' }}
                    id="proof-file-input"
                  />
                  <label htmlFor="proof-file-input" style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Upload sx={{ fontSize: 40, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Click to select file
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        PNG, JPG, PDF up to 10MB
                      </Typography>
                    </Box>
                  </label>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '2px solid',
                    borderColor: 'var(--secColor)',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <CheckCircle sx={{ fontSize: 24, color: 'var(--secColor)', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setSelectedFile(null)}
                    disabled={uploading}
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'error.dark',
                      },
                    }}
                  >
                    <Cancel />
                  </IconButton>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    style={{ display: 'none' }}
                    id="proof-file-input-change"
                  />
                  <label htmlFor="proof-file-input-change" style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
                    <MUIButton
                      size="small"
                      variant="outlined"
                      disabled={uploading}
                      sx={{ textTransform: 'none', flexShrink: 0 }}
                    >
                      Change
                    </MUIButton>
                  </label>
                </Box>
              )}
            </Box>

            {uploading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <CircularProgress size={24} sx={{ mr: 2, color: 'var(--secColor)' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Uploading...
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <MUIButton
                onClick={handleCloseUploadModal}
                disabled={uploading}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </MUIButton>
              <MUIButton
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                sx={{ ...main_button, textTransform: 'none' }}
              >
                {uploading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Upload'}
              </MUIButton>
            </Box>
          </Paper>
        </Fade>
      </Modal>

      {/* Password Verification Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handlePasswordClose}>
        <DialogTitle>Password Verification Required</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Please enter your password to confirm deletion.</Typography>
          <TextField
            margin="normal"
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={({ target }) => setPassword(target.value)}
            autoFocus
            error={Boolean(passwordError)}
            helperText={passwordError}
            disabled={passwordLoading}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !passwordLoading) {
                handlePasswordConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <MUIButton onClick={handlePasswordClose} disabled={passwordLoading} sx={{ textTransform: 'none' }}>
            Cancel
          </MUIButton>
          <MUIButton onClick={handlePasswordConfirm} disabled={passwordLoading} sx={{ ...main_button, textTransform: 'none' }}>
            {passwordLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Verify'}
          </MUIButton>
        </DialogActions>
      </Dialog>

      {/* Delete Transaction Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ fontWeight: 600, color: "error.main" }}>
          Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MUIButton
            onClick={handleCloseDeleteConfirm}
            disabled={deleting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </MUIButton>
          <MUIButton
            onClick={handleConfirmDeleteTransaction}
            disabled={deleting}
            sx={{
              ...main_button,
              bgcolor: 'error.main',
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'error.dark',
              },
            }}
            autoFocus
          >
            {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Delete'}
          </MUIButton>
        </DialogActions>
      </Dialog>

      {/* Delete Proof Confirmation Dialog */}
      <Dialog
        open={deleteProofConfirmOpen}
        onClose={handleCloseDeleteProofConfirm}
        aria-labelledby="delete-proof-dialog-title"
        aria-describedby="delete-proof-dialog-description"
      >
        <DialogTitle id="delete-proof-dialog-title" sx={{ fontWeight: 600, color: "error.main" }}>
          Delete Proof
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-proof-dialog-description">
            Are you sure you want to delete the proof for this transaction? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MUIButton
            onClick={handleCloseDeleteProofConfirm}
            disabled={deleting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </MUIButton>
          <MUIButton
            onClick={handleConfirmDeleteProof}
            disabled={deleting}
            sx={{
              ...main_button,
              bgcolor: 'error.main',
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'error.dark',
              },
            }}
            autoFocus
          >
            {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Delete'}
          </MUIButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
  token: state?.auth?.token,
  payouts: state?.records?.payouts,
  vendors: state?.records?.vendors
});

const mapDispatchToProps = { getPayouts, getVendors, getUploadUrl, addTransactionProof, deleteTransaction, deleteTransactionProof };

export default connect(mapStateToProps, mapDispatchToProps)(Payouts);
